import { useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Archive, ArchiveRestore, Plus, StickyNote, Users } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ColorDotStack } from "@/components/cinch/ColorDot";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/cinch/Field";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth/useCan";

import {
  useCreateSession,
  useSessions,
  useUpdateSession,
  type PlanningSession,
} from "../api";

export function PlanningSessionsPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const sessions = useSessions(includeArchived);
  const canWrite = useCan("planning.write");

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Exec Planning</h1>
          <p className="text-sm text-muted-foreground">
            Offsite planning canvases — SWOT walls and the Impact vs Effort grid.
            Everyone in the session posts sticky notes; the AI helps sort, question,
            and summarize them.
          </p>
        </div>
        {canWrite ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New session
          </Button>
        ) : null}
      </header>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Switch
          id="include-archived"
          checked={includeArchived}
          onCheckedChange={setIncludeArchived}
        />
        <label htmlFor="include-archived">Show archived</label>
      </div>

      {sessions.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading sessions…</p>
      ) : sessions.data?.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {sessions.data.map((s) => (
            <SessionCard key={s.id} session={s} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <StickyNote className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">No planning sessions yet</p>
            <p className="text-xs text-muted-foreground">
              You see the sessions you&apos;ve been invited to. If you&apos;re expecting
              one, ask the organizer to add you.
            </p>
          </CardContent>
        </Card>
      )}

      <CreateSessionDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function SessionCard({ session }: { session: PlanningSession }) {
  const navigate = useNavigate();
  const canWrite = useCan("planning.write");
  const canAdmin = useCan("planning.admin");
  const canManage = canWrite || canAdmin;
  const update = useUpdateSession();
  const archived = session.status === "archived";

  return (
    <Card
      className="cursor-pointer transition-colors hover:border-brand/50"
      onClick={() =>
        navigate({ to: "/planning/$sessionId", params: { sessionId: session.id } })
      }
    >
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold leading-tight">{session.name}</div>
          {archived ? <Badge variant="secondary">Archived</Badge> : null}
        </div>
        {session.description ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">{session.description}</p>
        ) : null}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <StickyNote className="h-3.5 w-3.5" />
            {session.note_count} notes
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ColorDotStack
              items={session.participants.map((p) => ({ color: p.color, title: p.display_name }))}
              max={8}
            />
            <Users className="h-3.5 w-3.5" />
            {session.participants.length}
          </span>
        </div>
        {canManage ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                update.mutate(
                  { id: session.id, status: archived ? "active" : "archived" },
                  {
                    onSuccess: () =>
                      toast.success(archived ? "Session restored" : "Session archived"),
                    onError: (err) => toast.error(err.message),
                  },
                );
              }}
            >
              {archived ? (
                <>
                  <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore
                </>
              ) : (
                <>
                  <Archive className="mr-1 h-3.5 w-3.5" /> Archive
                </>
              )}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CreateSessionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const create = useCreateSession();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const submit = () => {
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), description: description.trim() || null },
      {
        onSuccess: (s) => {
          onOpenChange(false);
          setName("");
          setDescription("");
          navigate({ to: "/planning/$sessionId", params: { sessionId: s.id } });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New planning session</DialogTitle>
          <DialogDescription>
            One session per offsite/workshop. It gets four SWOT boards and an
            Impact vs Effort grid.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field
            id="session-name"
            label="Session name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Textarea
            placeholder="What's this session about? (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
